// noinspection JSIgnoredPromiseFromCall

import {EventEmitter, Injectable} from "@angular/core";
import {SettingsStorageService} from "../../settings-storage-service.service";
import {UserSettingsService} from "../../user-settings.service";

const rootKey = 'visible-offsets.';
const subKeyPriceBox = 'price-box.';
const subKeyZonesGrid = 'zones-grid.';
const showOffsetsKey = 'show-offsets';
const showModeKey = 'show-mode';
const showSpreadOffsetKey = 'show-spread-offset';
const showSpreadWidthKey = 'show-spread-width';
const showSecondSpreadOffsetKey = 'show-2nd-spread-offset';
const showSecondSpreadWidthKey = 'show-2nd-spread-width';
const showProtectiveOptionOffsetKey = 'show-protective-option-offset';
const showSecondProtectiveOptionOffsetKey = 'show-2nd-protective-option-offset';
const showSingleValueKey = 'show-single-value';
const spreadOffsetFilter = 'spread-offset-filter';
const spreadWidthFilter = 'spread-width-filter';
const showImpliedVolatility = 'implied-volatility';
const showZonesGridArrow = 'zones-grid-arrow';
const showPricingStepsIcon = 'pricing-steps-icon';
const favoriteAdjustments = 'favorite-adjustments';
// CR-20260904-001 (Presentation, BR-P2/BR-P4): the three Price Box landmine highlight switches.
// They live here — with the Price Box's own settings — after the owner overturned their earlier
// placement in Application Settings. Same persistence as every key above: UserSettingsService,
// i.e. PER USER, so the move changed where they are edited and NOT who they belong to.
const highlightMondays = 'highlight-mondays';
const highlightTier1 = 'highlight-tier-1';
const highlightTier2 = 'highlight-tier-2';
// Follow-on 2026-09-08 (O73): the fourth switch — the events-list tooltip on a coloured header.
// Super users only, on the dialog and on the paint; the key is still per user like the other three.
const showLandmineEventsTooltip = 'show-landmine-events-tooltip';

export type VisibleOffsetsShowMode = 'Mouse Over' | 'Displayed';

type OffsetManagerType = 'PriceBox' | 'Zones Grid';

export class OffsetsManager {

    constructor(
        private readonly _userSettingsService: UserSettingsService,
        private readonly _type: OffsetManagerType
    ) {
    }

    updated$ = new EventEmitter();

    get showOffsets(): boolean {
        return this.getBoolean(showOffsetsKey);
    }

    set showOffsets(v: boolean) {
        this.setBoolean(showOffsetsKey, v);
    }

    get showMode(): VisibleOffsetsShowMode {
        return this.getString(showModeKey) || 'Mouse Over';
    }

    set showMode(v: VisibleOffsetsShowMode) {
        this.setString(showModeKey, v);
    }

    get showSingleValue(): boolean {
        return this.getBoolean(showSingleValueKey);
    }

    set showSingleValue(v: boolean) {
        this.setBoolean(showSingleValueKey, v);
    }

    get showSpreadOffset(): boolean {
        return this.getBoolean(showSpreadOffsetKey);
    }

    set showSpreadOffset(v: boolean) {
        this.setBoolean(showSpreadOffsetKey, v);
    }

    get showSpreadWidth(): boolean {
        return this.getBoolean(showSpreadWidthKey);
    }

    set showSpreadWidth(v: boolean) {
        this.setBoolean(showSpreadWidthKey, v);
    }

    get showSecondSpreadOffset(): boolean {
        return this.getBoolean(showSecondSpreadOffsetKey);
    }

    set showSecondSpreadOffset(v: boolean) {
        this.setBoolean(showSecondSpreadOffsetKey, v);
    }

    get showSecondSpreadWidth(): boolean {
        return this.getBoolean(showSecondSpreadWidthKey);
    }

    set showSecondSpreadWidth(v: boolean) {
        this.setBoolean(showSecondSpreadWidthKey, v);
    }

    get showProtectiveOptionOffset(): boolean {
        return this.getBoolean(showProtectiveOptionOffsetKey);
    }

    set showProtectiveOptionOffset(v: boolean) {
        this.setBoolean(showProtectiveOptionOffsetKey, v);
    }

    get showSecondProtectiveOptionOffset(): boolean {
        return this.getBoolean(showSecondProtectiveOptionOffsetKey);
    }

    set showSecondProtectiveOptionOffset(v: boolean) {
        this.setBoolean(showSecondProtectiveOptionOffsetKey, v);
    }

    get spreadOffsetFilter(): number {
        return this.getNumber(spreadOffsetFilter);
    }

    set spreadOffsetFilter(v: number) {
        this.setNumber(spreadOffsetFilter, v);
    }

    get spreadWidthFilter(): number {
        return this.getNumber(spreadWidthFilter);
    }

    set spreadWidthFilter(v: number) {
        this.setNumber(spreadWidthFilter, v);
    }

    get showImpliedVolatility(): boolean {
        return this.getBoolean(showImpliedVolatility);
    }

    set showImpliedVolatility(v: boolean) {
        this.setBoolean(showImpliedVolatility, v);
    }

    get showZonesGridArrow(): boolean {
        return this.getBoolean(showZonesGridArrow);
    }

    set showZonesGridArrow(v: boolean) {
        this.setBoolean(showZonesGridArrow, v);
    }

    get showPricingStepsIcon(): boolean {
        const fullKey = this.getFullKey(showPricingStepsIcon);
        const v = this._userSettingsService.getValue<boolean>(fullKey);
        return v !== false;
    }

    set showPricingStepsIcon(v: boolean) {
        this.setBoolean(showPricingStepsIcon, v);
    }

    get favoriteAdjustments() : string {
        return this.getString(favoriteAdjustments);
    }

    set favoriteAdjustments(v: string) {
        this.setString(favoriteAdjustments, v);
    }

    // CR-20260904-001 (Presentation): the three landmine highlight switches. `getBoolean` returns
    // `v || false`, so a user whose stored settings predate this CR reads OFF — the default the rules
    // require (BR-P2), with no migration and no seeding.
    get highlightMondays(): boolean {
        return this.getBoolean(highlightMondays);
    }

    set highlightMondays(v: boolean) {
        this.setBoolean(highlightMondays, v);
    }

    get highlightTier1(): boolean {
        return this.getBoolean(highlightTier1);
    }

    set highlightTier1(v: boolean) {
        this.setBoolean(highlightTier1, v);
    }

    get highlightTier2(): boolean {
        return this.getBoolean(highlightTier2);
    }

    set highlightTier2(v: boolean) {
        this.setBoolean(highlightTier2, v);
    }

    get showLandmineEventsTooltip(): boolean {
        return this.getBoolean(showLandmineEventsTooltip);
    }

    set showLandmineEventsTooltip(v: boolean) {
        this.setBoolean(showLandmineEventsTooltip, v);
    }



    private getBoolean(key: string): boolean {
        const fullKey = this.getFullKey(key);
        const v = this._userSettingsService.getValue<boolean>(fullKey);
        return v || false;
    }

    private setBoolean(key: string, value: boolean) {
        const fullKey = this.getFullKey(key);
        this._userSettingsService.setValue(fullKey, value);
        this.updated$.emit();
    }

    private getString(key: string): any {
        const fullKey = this.getFullKey(key);
        const v = this._userSettingsService.getValue<string>(fullKey);
        return v;
    }

    private setString(key: string, value: any) {
        const fullKey = this.getFullKey(key);
        this._userSettingsService.setValue(fullKey, value);
        this.updated$.emit();
    }

    private getNumber(key: string): number {
        const fullKey = this.getFullKey(key);
        const v = this._userSettingsService.getValue<number>(fullKey);
        return v;
    }

    private setNumber(key: string, value: number) {
        const fullKey = this.getFullKey(key);
        this._userSettingsService.setValue(fullKey, value);
        this.updated$.emit();
    }

    private getFullKey(key: string) {
        const subKey = this._type === 'PriceBox' ? subKeyPriceBox : subKeyZonesGrid;
        const fullKey = rootKey + subKey + key;
        return fullKey;
    }

}

@Injectable()
export class VisibleOffsetsSettingsService {
    constructor(
        userSettingsService: UserSettingsService
    ) {

        this.priceBox = new OffsetsManager(userSettingsService, 'PriceBox');

        this.zonesGrid = new OffsetsManager(userSettingsService, 'Zones Grid');

        this.priceBox.updated$.subscribe(x => {
            this.priceBoxUpdated$.emit();
        });

        this.zonesGrid.updated$.subscribe(x => {
            this.zonesGridUpdated$.emit();
        });
    }

    priceBoxUpdated$ = new EventEmitter();

    zonesGridUpdated$ = new EventEmitter();

    readonly priceBox: OffsetsManager;

    readonly zonesGrid: OffsetsManager;
}
